import API from "serviceshift-ui/api-client";
import { trackEvent } from "@/lib/pendo";

// Sends a customer a no-login link to a job's open quotes. The URL comes from
// the job payload (`job.links.send_quotes_link`), so no path is hardcoded.

export type QuotesLinkChannel = "sms" | "email" | "both";

interface SendQuotesLinkParams {
  url: string;
  via: QuotesLinkChannel;
  phoneNumber?: string;
  email?: string;
}

export interface SendQuotesLinkResult {
  sent: boolean;
  job_id: number;
  delivered: Array<"sms" | "email">;
  expires_at: string;
}

// The channel lives in the event name: Pendo can't break a track event out by
// property (see paymentLinks.ts).
const QUOTES_LINK_SENT_EVENTS = {
  sms: "quotes_link_sent_sms",
  email: "quotes_link_sent_email",
  both: "quotes_link_sent_both"
} as const;

// Telemetry must never fail the send: a throw here would make the modal report
// a failure for a link the customer already received.
function trackQuotesLinkSent(
  via: QuotesLinkChannel,
  result: SendQuotesLinkResult
): void {
  if (!result?.sent) return;

  try {
    trackEvent(QUOTES_LINK_SENT_EVENTS[via], {
      via,
      job_id: result.job_id,
      delivered: result.delivered.join(",")
    });
  } catch {
    // analytics is best-effort
  }
}

export default {
  async sendQuotesLink(
    _: unknown,
    { url, via, phoneNumber, email }: SendQuotesLinkParams
  ): Promise<SendQuotesLinkResult> {
    const data: Record<string, string> = { via };
    if (phoneNumber) data.phone_number = phoneNumber;
    if (email) data.email = email;

    const response = await API.makeRequest<SendQuotesLinkResult>(url, {
      method: "POST",
      data
    });
    trackQuotesLinkSent(via, response.data);
    return response.data;
  }
};
